import * as THREE from "three";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { PLYLoader } from "three/addons/loaders/PLYLoader.js";
import { MeasureTool } from "../tools/MeasureTool.ts";

let _dracoLoader: DRACOLoader | null = null;

function getDracoLoader(): DRACOLoader {
  if (!_dracoLoader) {
    _dracoLoader = new DRACOLoader();
    _dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");
  }
  return _dracoLoader;
}

export async function loadGlb(url: string): Promise<THREE.Group> {
  const loader = new GLTFLoader();
  loader.setDRACOLoader(getDracoLoader());

  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => {
        const scene = gltf.scene;
        // RTABmap GLB is Z-up; rotate to Three.js Y-up (same as PLY)
        scene.rotation.x = -Math.PI / 2;
        MeasureTool.buildBVH(scene);
        resolve(scene);
      },
      undefined,
      (err) => reject(err),
    );
  });
}

/**
 * Load a RoomPlan `_model.glb` (walls + openings + objects, already Y-up and
 * in RoomPlan's native metre coordinates). No Z→Y rotation is applied so the
 * transforms from `_rooms.json` stay consistent with the loaded scene.
 */
export async function loadRoomPlanGlb(url: string): Promise<THREE.Group> {
  const loader = new GLTFLoader();
  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => resolve(gltf.scene),
      undefined,
      (err) => reject(err),
    );
  });
}

export async function loadPly(url: string): Promise<THREE.Group> {
  const loader = new PLYLoader();

  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (geometry) => {
        geometry.computeVertexNormals();

        const hasVertexColors = geometry.hasAttribute("color");
        const material = new THREE.MeshStandardMaterial({
          vertexColors: hasVertexColors,
          ...(hasVertexColors ? {} : { color: 0xcccccc }),
          side: THREE.DoubleSide,
        });

        const mesh = new THREE.Mesh(geometry, material);
        const group = new THREE.Group();
        group.add(mesh);
        // PLY from RTABmap is Z-up; rotate to Three.js Y-up
        group.rotation.x = -Math.PI / 2;

        MeasureTool.buildBVH(group);
        resolve(group);
      },
      undefined,
      (err) => reject(err),
    );
  });
}

export async function loadModel(url: string, format: "glb" | "ply"): Promise<THREE.Group> {
  return format === "ply" ? loadPly(url) : loadGlb(url);
}

/**
 * Detect floor Y level using a histogram of vertex heights.
 * Builds bins of BIN_SIZE, then picks the first bin from the bottom
 * that contains at least MIN_PEAK_RATIO of all vertices.
 */
function detectFloorY(model: THREE.Group, box: THREE.Box3): number {
  const BIN_SIZE = 0.05; // 5 cm
  const MIN_PEAK_RATIO = 0.005; // 0.5 % of total vertices

  const minY = box.min.y;
  const range = box.max.y - minY;
  if (range < 0.001) return minY;

  const numBins = Math.ceil(range / BIN_SIZE);
  const bins = new Uint32Array(numBins);
  let totalVerts = 0;
  const v = new THREE.Vector3();

  model.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const pos = obj.geometry.getAttribute("position");
    if (!pos) return;
    const wm = obj.matrixWorld;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      v.applyMatrix4(wm);
      const bin = Math.min(Math.floor((v.y - minY) / BIN_SIZE), numBins - 1);
      bins[bin]++;
      totalVerts++;
    }
  });

  if (totalVerts === 0) return minY;

  const threshold = totalVerts * MIN_PEAK_RATIO;
  for (let i = 0; i < numBins; i++) {
    if (bins[i] >= threshold) {
      return minY + (i + 0.5) * BIN_SIZE;
    }
  }
  return minY;
}

/**
 * Detect ceiling Y level using a histogram of vertex heights.
 * Similar to detectFloorY but searches from the top for a dense bin.
 */
export function detectCeilingY(model: THREE.Group, box: THREE.Box3): number {
  const BIN_SIZE = 0.05;
  const MIN_PEAK_RATIO = 0.005;

  const minY = box.min.y;
  const range = box.max.y - minY;
  if (range < 0.001) return box.max.y;

  const numBins = Math.ceil(range / BIN_SIZE);
  const bins = new Uint32Array(numBins);
  let totalVerts = 0;
  const v = new THREE.Vector3();

  model.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const pos = obj.geometry.getAttribute("position");
    if (!pos) return;
    const wm = obj.matrixWorld;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      v.applyMatrix4(wm);
      const bin = Math.min(Math.floor((v.y - minY) / BIN_SIZE), numBins - 1);
      bins[bin]++;
      totalVerts++;
    }
  });

  if (totalVerts === 0) return box.max.y;

  const threshold = totalVerts * MIN_PEAK_RATIO;
  // Search from top down for the first dense bin (ceiling)
  for (let i = numBins - 1; i >= 0; i--) {
    if (bins[i] >= threshold) {
      return minY + (i + 0.5) * BIN_SIZE;
    }
  }
  return box.max.y;
}

export function centerModel(model: THREE.Group): THREE.Vector3 {
  const box = new THREE.Box3().setFromObject(model);
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  box.getCenter(center);
  box.getSize(size);

  const floorY = detectFloorY(model, box);

  // Center X/Z at geometric center, set Y so floor = 0
  model.position.x -= center.x;
  model.position.z -= center.z;
  model.position.y -= floorY;

  return size;
}

export function collectMeshes(root: THREE.Object3D): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  root.traverse((obj) => {
    if (obj instanceof THREE.Mesh) meshes.push(obj);
  });
  return meshes;
}

export function disposeModel(model: THREE.Group): void {
  model.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      if (obj.geometry.boundsTree) obj.geometry.disposeBoundsTree();
      obj.geometry.dispose();
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const m of mats) {
        if (m.map) m.map.dispose();
        m.dispose();
      }
    }
  });
}
